import { useEffect, useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { getProfitLossTrend } from "services/finance.service";

const isCustomRangeIncomplete = (filters) =>
  filters?.dateRangeType === "custom" &&
  (!filters?.customDateRange?.[0] || !filters?.customDateRange?.[1]);

const formatMonth = (dateStr) => {
  if (!dateStr) return "";
  const date = new Date(`${dateStr}-01`);
  return date.toLocaleDateString("en-US", { month: "short", year: "numeric" });
};

const formatCurrency = (value) =>
  `$${Number(value || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const formatCurrencyAxis = (value) => {
  if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `$${(value / 1_000).toFixed(1)}K`;
  return `$${value}`;
};

const SERIES = [
  { key: "revenue", label: "Revenue", color: "#4CAF50" },
  { key: "expense", label: "Expenses", color: "#F44336" },
  { key: "commission", label: "Commissions", color: "#FF9800" },
];

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;

  return (
    <div className="bg-white p-3 border border-gray-200 shadow-sm rounded-md">
      <p className="text-sm font-medium text-gray-900 mb-1">{formatMonth(label)}</p>
      {payload.map((entry) => (
        <p key={entry.dataKey} className="text-sm" style={{ color: entry.color }}>
          <span className="font-medium">{entry.name}:</span>{" "}
          {formatCurrency(entry.value)}
        </p>
      ))}
    </div>
  );
};

export default function RevenueExpenseProfitTrendChart({ api, filters, compact = false }) {
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 640);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (isCustomRangeIncomplete(filters)) {
      setIsLoading(false);
      setData([]);
      setError(null);
      return;
    }

    let cancelled = false;

    const load = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const result = await getProfitLossTrend(api, { filters });
        const timeSeries = result?.timeSeries ?? [];
        if (!cancelled) setData(timeSeries);
      } catch {
        if (!cancelled) {
          setData([]);
          setError("Failed to load financial trends. Please try again.");
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [filters.dateRangeType, filters.customDateRange?.[0], filters.customDateRange?.[1]]);

  const chartHeight = compact ? 220 : isMobile ? 250 : 320;
  const chartMinWidth =
    isMobile && data.length > 0 ? Math.max(data.length * 56, 320) : "100%";

  const wrapperClass = compact
    ? ""
    : "bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden mb-6";

  const content = (
    <>
      {!compact && (
        <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
          <h3 className="text-sm font-medium text-gray-900">Revenue vs Expenses</h3>
        </div>
      )}

      <div className={compact ? "" : "p-4"}>
        {error && (
          <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        )}

        {isLoading ? (
          <div className="animate-pulse">
            <div className="h-4 w-1/3 bg-gray-200 rounded mb-6" />
            <div className="bg-gray-200 rounded" style={{ height: chartHeight }} />
          </div>
        ) : isCustomRangeIncomplete(filters) ? (
          <div
            className="flex items-center justify-center text-gray-500"
            style={{ height: chartHeight }}
          >
            <p className="text-sm italic">
              Select start and end dates to view financial trends.
            </p>
          </div>
        ) : error ? (
          <div
            className="flex items-center justify-center text-gray-500"
            style={{ height: chartHeight }}
          >
            <p className="text-sm italic">
              Financial trends unavailable due to a loading error.
            </p>
          </div>
        ) : data.length === 0 ? (
          <div
            className="flex items-center justify-center text-gray-500"
            style={{ height: chartHeight }}
          >
            <p>No trend data available for the selected period.</p>
          </div>
        ) : (
          <div className={isMobile ? "overflow-x-auto" : ""}>
            <div style={{ minWidth: chartMinWidth, height: chartHeight }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={data}
                  margin={{
                    top: 10,
                    right: 10,
                    left: isMobile ? 0 : 0,
                    bottom: isMobile ? 0 : 0,
                  }}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    vertical={false}
                    stroke="#E5E7EB"
                  />
                  <XAxis
                    dataKey="date"
                    tickFormatter={formatMonth}
                    tick={{ fontSize: isMobile ? 10 : 12, fill: "#6B7280" }}
                    axisLine={{ stroke: "#E5E7EB" }}
                    tickLine={{ stroke: "#E5E7EB" }}
                    angle={isMobile ? -45 : 0}
                    textAnchor={isMobile ? "end" : "middle"}
                    height={isMobile ? 60 : 30}
                    interval={isMobile ? "preserveStartEnd" : 0}
                  />
                  <YAxis
                    tickFormatter={formatCurrencyAxis}
                    tick={{ fontSize: isMobile ? 10 : 12, fill: "#6B7280" }}
                    axisLine={{ stroke: "#E5E7EB" }}
                    tickLine={{ stroke: "#E5E7EB" }}
                    width={isMobile ? 48 : 60}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  {!compact && <Legend />}
                  {SERIES.map(({ key, label, color }) => (
                    <Bar
                      key={key}
                      dataKey={key}
                      name={label}
                      fill={color}
                      radius={[4, 4, 0, 0]}
                    />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
      </div>
    </>
  );

  return compact ? content : <div className={wrapperClass}>{content}</div>;
}
